import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';

import {
  GetAccountWidgetBirthdaysResponse,
  UpdateWidgetBirthdaysRequest,
  WidgetBirthdays,
} from '@app/gen/dashboard';

import { BirthdaysFormValues } from '../../types';

@injectable()
export class SettingsBirthdaysMapper {
  constructor(private format: FormatService) {}

  private processSettingsToInitialValues(
    settings?: WidgetBirthdays,
  ): Record<string, string> {
    if (!settings) return {};

    return Object.fromEntries(
      Object.entries(settings).map(([key, value]) => [
        key,
        this.processValueToString(value),
      ]),
    );
  }

  private processValueToString(value: boolean | string | undefined): string {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return String(value ?? '');
  }

  private processValuesToSettings(
    values: Record<string, string>,
  ): Record<string, boolean | string> {
    return Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        key.endsWith('_level') ? value : this.format.toBoolean(value),
      ]),
    );
  }

  processBirthdaysSettings(
    response: GetAccountWidgetBirthdaysResponse,
  ): BirthdaysFormValues {
    const settings = response.scope_settings;
    const allValues = this.processSettingsToInitialValues(settings);

    const employeeKeys = Object.keys(allValues).filter((k) =>
      k.startsWith('employee_'),
    );
    const managerKeys = Object.keys(allValues).filter((k) =>
      k.startsWith('manager_'),
    );

    return {
      employee_settings: Object.fromEntries(
        employeeKeys.map((k) => [k, allValues[k]]),
      ),
      manager_settings: Object.fromEntries(
        managerKeys.map((k) => [k, allValues[k]]),
      ),
    };
  }

  processBirthdaysSettingsValues(
    values: BirthdaysFormValues,
  ): UpdateWidgetBirthdaysRequest {
    return {
      scope_settings: {
        ...this.processValuesToSettings(values.employee_settings),
        ...this.processValuesToSettings(values.manager_settings),
      } as unknown as WidgetBirthdays,
    };
  }
}
